import {
  DEFAULT_DEBOUNCE_DELAY,
  IPaginationMetadata,
  STATUSPAGE,
} from '@repo/ui';
import debounce from 'debounce';
import { GetGroupListUseCaseInterface } from 'domain/usecases';
import { GROUPS_ORDER_COLUMNS } from 'domain/value-objects/groups-order-columns';
import { ORDER_TYPE } from 'domain/value-objects/order-type';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearGroupList,
  loadGroupList,
} from 'presentation/config/store/groups/group-list-slice';
import { useCallback, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

export const useGroupList = () => {
  // Hooks
  const { groupService } = useService();
  const dispatch = useDispatch();
  const { getLegends } = useLegends();
  const navigate = useNavigate();
  // const { filtersDisabled, filterBySemester, filterByCurricularUnit } =
  //   useFiltersPermits();

  // States
  const { groupsListData, groupsListFilters } = useAppSelector(
    (state) => state.groupList,
  );
  // const { semestersUser } = useAppSelector((state) => state.semesters);
  // const { curricularUnitsUser } = useAppSelector(
  //   (state) => state.curricularUnits,
  // );
  const [paginationMetadata, setPaginationMetadata] = useState<
    IPaginationMetadata<GROUPS_ORDER_COLUMNS>
  >({
    selectedPage: 0,
    rowsPerPage: 10,
    searchString: '',
    orderColumn: GROUPS_ORDER_COLUMNS.NAME,
    sortDirection: ORDER_TYPE.ASC,
  });
  const [listLoadingStatus, setListLoadingStatus] = useState<STATUSPAGE>();

  // Methods
  const handleGetGroupList = (params: GetGroupListUseCaseInterface.Params) => {
    setListLoadingStatus(STATUSPAGE.LOADING);
    return groupService
      .getGroupList(params)
      .then((res) => {
        dispatch(loadGroupList(res));
      })
      .catch((err) => {
        dispatch(clearGroupList());
        notifyError(err.message || 'Ocorreu um erro.');
      })
      .finally(() => {
        setListLoadingStatus(STATUSPAGE.DONE);
      });
  };

  const changePaginationMetadata = useCallback(
    <T extends keyof IPaginationMetadata<GROUPS_ORDER_COLUMNS>>(
      key: T,
      value: IPaginationMetadata<GROUPS_ORDER_COLUMNS>[T],
    ) => {
      setPaginationMetadata((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const handleSortRemote = useCallback(
    (
      selectedColumn: TableColumn<
        GetGroupListUseCaseInterface.Result['data'][number]
      >,
      sortDirection: SortOrder,
    ) => {
      changePaginationMetadata(
        'orderColumn',
        selectedColumn.sortField as GROUPS_ORDER_COLUMNS,
      );
      changePaginationMetadata(
        'sortDirection',
        sortDirection.toUpperCase() as ORDER_TYPE,
      );
      changePaginationMetadata('selectedPage', 0);
    },
    [],
  );

  const handlePageChange = useCallback((page: number) => {
    changePaginationMetadata('selectedPage', page - 1);
  }, []);

  const handleSearchRemote = useCallback((searchString: string) => {
    changePaginationMetadata('searchString', searchString);
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    changePaginationMetadata('rowsPerPage', rowsPerPage);
  }, []);

  const debouncedRequestGetAllActivitiesByStatus = useMemo(
    () =>
      debounce((valuesToSubmit: GetGroupListUseCaseInterface.Params) => {
        handleGetGroupList(valuesToSubmit);
      }, DEFAULT_DEBOUNCE_DELAY),
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: groupsListFilters?.entity || null,
      course: groupsListFilters?.course || null,
      semester: groupsListFilters?.semester || null,
      unit: groupsListFilters?.curricularUnit || null,
    },

    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
    }),

    // There is no onSubmit function on this form (no submit button).
    // The API request is sent everytime any filter changes, so it is controlled via useEffect and DataTable's remote pagination.
    onSubmit: () => {
      return;
    },
  });

  return {
    listLoadingStatus,
    // filtersDisabled,
    // filterBySemester,
    // filterByCurricularUnit,
    groupsListData,
    validation,
    paginationMetadata,
    // semestersUser,
    // curricularUnitsUser,
    debouncedRequestGetAllActivitiesByStatus,
    handleGetGroupList,
    setListLoadingStatus,
    navigate,
    getLegends,
    handleSortRemote,
    handleSearchRemote,
    handlePageChange,
    handleRowsPerPageChange,
  };
};
